// SETTINGS V2 — demo-pass handoff panel (DEMO_USER_TYPE_SPEC F1).
//
// Replaces the mint form inside IssueGuestPassDialogV2 once a demo pass exists.
// This is the surface the owner uses LIVE on a Teams call: they read the PIN
// aloud, the visitor scans the machine QR and types it in. Legibility of the PIN
// and of the expiry beats styling flourish everywhere in this file.
//
// Three security postures are baked into the layout, not bolted on:
//  1. REVEAL-ON-DEMAND. The PIN renders masked. A recorded/screen-shared call
//     would otherwise capture it the instant the dialog opens, and the PIN is not
//     single-use — anyone who hears it gets their own independent session
//     (RedeemCount is a counter, not a limit). The owner reveals it when ready
//     and can hide it again before sharing their screen.
//  2. TWO SEPARATE EXPIRY FACTS. `expiresAt` is when the PIN stops working; the
//     server-side session cap is how long ONE sign-in lasts and is much shorter.
//     Conflating them would misrepresent when access actually dies. The cap is
//     only ever quoted when the server sent the number (`sessionCapHours`).
//  3. REVOKE IS NOT AN INSTANT KILL. Nine services re-check liveness and cut on
//     the next request; MIDAS chat lives on the Account service and keeps
//     answering until the token expires. The note says exactly that.
//
// GP-MENU §7.3 reuses this panel for the "Nový PIN" handoff (RegeneratePinDialogV2)
// instead of forking it — the reveal/copy/expiry/fault mechanics above are exactly
// what that surface needs. Two additive props, both defaulting to the mint
// behaviour so the demo MINT path is unchanged:
//   • `variant="guest"` — a guest pass has no demo session cap, so the cap
//     sub-block is omitted (quoting a demo cap on a guest pass would be wrong)
//     and the steps line uses the guest-kiosk wording.
//   • `showSessionSurvivalNote` — [SEC-REQ GP-5] renders `regenSessionNote` for
//     BOTH variants on the regenerate path: a new PIN does NOT end sessions that
//     are already signed in, and an operator regenerating because a PIN leaked
//     must not conclude they cut access.
//
// Inline styles + scoped CSS-var tokens; no MUI/sx/dt/Tailwind.
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { GuestPass } from "../../models/guestPassModels";
import { formatRemaining, groupPin, isClosingSoon } from "../demoPassUtils";

export interface DemoPassHandoffV2Props {
  pass: GuestPass;
  /** "demo" (default) = the mint path, unchanged. "guest" drops the demo session
   *  cap block and uses the guest-kiosk steps wording. */
  variant?: "demo" | "guest";
  /** [SEC-REQ GP-5] Regenerate path only — adds the session-survival note. */
  showSessionSurvivalNote?: boolean;
}

const DemoPassHandoffV2: React.FC<DemoPassHandoffV2Props> = ({
  pass,
  variant = "demo",
  showSessionSurvivalNote = false,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();

  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  // Re-render every 30s so the "ends in ..." reading stays true while the owner
  // is mid-call. Cheap, and stale timing here is exactly what misleads.
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const isGuestVariant = variant === "guest";
  const pin = pass.pinCode || "";
  const groups = groupPin(pin);
  const remaining = formatRemaining(pass.expiresAt);
  const closingSoon = isClosingSoon(pass.expiresAt);

  const handleCopy = async () => {
    if (!pin) return;
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure context / permission) — the digits are on
      // screen and read aloud anyway, so this stays silent by design.
    }
  };

  const label: React.CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: "var(--faint)",
  };

  // ── PIN hero ──────────────────────────────────────────────────────────────
  const pinDigits = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isPhone ? 14 : 22,
        fontFamily: "var(--font-mono)",
        fontSize: isPhone ? 38 : 50,
        fontWeight: 700,
        lineHeight: 1.1,
        color: "var(--ink)",
        letterSpacing: isPhone ? "0.10em" : "0.14em",
        userSelect: "all",
      }}
      aria-label={groups.join(" ").split("").join(" ")}
    >
      {groups.map((g, i) => (
        <span key={i}>{g}</span>
      ))}
    </div>
  );

  const pinMasked = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isPhone ? 14 : 22,
        fontFamily: "var(--font-mono)",
        fontSize: isPhone ? 38 : 50,
        fontWeight: 700,
        lineHeight: 1.1,
        color: "var(--faint)",
        letterSpacing: isPhone ? "0.10em" : "0.14em",
      }}
      aria-hidden="true"
    >
      <span>•••</span>
      <span>•••</span>
    </div>
  );

  const pinBlock = (
    <div
      style={{
        // Amber frame when the PIN is missing — that state is a fault, not a
        // variant, and must not read as the normal happy path.
        border: `1px solid ${
          !pin || closingSoon ? "var(--amber)" : "var(--mod-accent-line)"
        }`,
        background: "var(--mod-accent-soft)",
        borderRadius: 12,
        padding: isPhone ? "16px 14px" : "18px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        textAlign: "center",
      }}
    >
      <div style={label}>{t("guestPass.handoffPinLabel") as string}</div>

      {pin ? (
        <>
          {revealed ? pinDigits : pinMasked}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              type="button"
              className="btn"
              onClick={() => setRevealed((v) => !v)}
              style={{
                background: revealed ? "transparent" : "var(--mod-accent)",
                borderColor: "var(--mod-accent)",
                color: revealed ? "var(--ink)" : "var(--mod-accent-fg)",
              }}
            >
              {(revealed ? t("guestPass.handoffHide") : t("guestPass.handoffReveal")) as string}
            </button>
            {revealed && (
              <button type="button" className="btn btn-ghost" onClick={handleCopy}>
                {(copied ? t("guestPass.handoffCopied") : t("guestPass.handoffCopy")) as string}
              </button>
            )}
          </div>
          {!revealed && (
            <div style={{ fontSize: 11.5, color: "var(--muted)", maxWidth: 340 }}>
              {t("guestPass.handoffRevealHint") as string}
            </div>
          )}
        </>
      ) : (
        // No PIN in the response. Demo passes send NO email (owner ruling
        // 2026-08-04), so there is no second channel to point at — this is a
        // fault, and the only recovery is to mint again. Rendered as such rather
        // than as a calm alternative, so the operator does not stand on a call
        // waiting for a mail that will never arrive.
        <>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--amber)" }}>
            {t("guestPass.handoffPinMissing") as string}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-2)", maxWidth: 380, lineHeight: 1.5 }}>
            {t("guestPass.handoffPinMissingHint") as string}
          </div>
        </>
      )}
    </div>
  );

  // ── Expiry: the two facts, kept visually distinct ─────────────────────────
  const expiryBlock = (
    <div
      style={{
        border: `1px solid ${closingSoon ? "var(--amber)" : "var(--border)"}`,
        borderRadius: 10,
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "var(--card-2)",
      }}
    >
      <div>
        <div style={label}>{t("guestPass.handoffPinValidLabel") as string}</div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: isPhone ? 16 : 18,
            fontWeight: 700,
            color: closingSoon ? "var(--amber)" : "var(--ink)",
            marginTop: 3,
          }}
        >
          {dayjs(pass.expiresAt).format("DD.MM.YYYY HH:mm")}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
          {
            (remaining
              ? t("guestPass.handoffEndsIn", { remaining })
              : t("guestPass.handoffEnded")) as string
          }
        </div>
      </div>

      {/* Demo only. A guest session tracks the pass window shown above — there is
          no separate demo cap to quote, and inventing one would misstate when
          access ends. */}
      {!isGuestVariant && (
        <div style={{ borderTop: "1px solid var(--border-soft)", paddingTop: 9 }}>
          <div style={label}>{t("guestPass.handoffSessionLabel") as string}</div>
          <div style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 3, lineHeight: 1.5 }}>
            {
              (pass.sessionCapHours
                ? t("guestPass.handoffSessionCapped", { hours: pass.sessionCapHours })
                : t("guestPass.handoffSessionCappedUnknown")) as string
            }
          </div>
        </div>
      )}
    </div>
  );

  const detailRow = (labelKey: string, value: string) => (
    <div style={{ display: "flex", gap: 10, alignItems: "baseline", minWidth: 0 }}>
      <span style={{ ...label, flex: "0 0 84px" }}>{t(labelKey) as string}</span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: "var(--font-mono)",
          fontSize: 12.5,
          color: "var(--ink)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );

  const note = (text: string) => (
    <div style={{ fontSize: 11.5, color: "var(--muted)", lineHeight: 1.55 }}>{text}</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
        {
          (isGuestVariant
            ? t("guestPass.handoffStepsGuest")
            : t("guestPass.handoffSteps")) as string
        }
      </div>

      {pinBlock}
      {expiryBlock}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {detailRow("guestPass.handoffMachine", pass.machineInternalNumber)}
        {detailRow("guestPass.handoffVisitor", pass.guestName || pass.guestEmail)}
      </div>

      <div
        style={{
          borderTop: "1px solid var(--border-soft)",
          paddingTop: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {note(t("guestPass.handoffShareNote") as string)}
        {note(t("guestPass.handoffLockoutNote") as string)}
        {note(t("guestPass.handoffRevokeNote") as string)}
        {/* [SEC-REQ GP-5] Same register and position as handoffRevokeNote, for
            BOTH variants: the new PIN kills the old PIN, NOT the sessions someone
            already signed into. Only "Zrušiť" cuts access now. */}
        {showSessionSurvivalNote && note(t("guestPass.regenSessionNote") as string)}
      </div>
    </div>
  );
};

export default DemoPassHandoffV2;
